import { useCallback, useEffect, useRef, useState } from "react";
import { LocatedVelavuDevice, VelavuSite } from "velavu-js-api";
import { useDeviceMovementListener } from "velavu-react";
import DeviceMarker, {
	deviceToDeviceMarker,
	resolveDeviceMarkerCoordinates,
	ViewportPadding,
} from "../data/device-marker";
import { getLocationFloorID } from "../helper/floor-helper";

const minDeviceMovementInterval = 500;
const maxDeviceMovementInterval = 5 * 1000;

export interface SiteFloorFilter {
	siteID: string;
	floorID: string;
}

/**
 * useDeviceMarkers maintains a mapping of PairedLocatedVelavuDevice[] to DeviceMarker[],
 * while keeping up-to-date with live location updates.
 * @param devices The devices to map to VelavuDeviceMarker objects
 * @param sites An array of all loaded sites to use for lookups
 * @param mapDimensions The current dimensions of the map view
 * @param siteFloorFilters A mapping of siteID to floorID for floor filtering
 */
export default function useDeviceMarkers(
	devices: LocatedVelavuDevice[],
	sites: VelavuSite[] | undefined,
	mapDimensions: [number, number],
	viewportPadding: ViewportPadding,
	siteFloorFilters: { [siteID: string]: string | undefined } | undefined,
): DeviceMarker[] {
	const [deviceMarkers, setDeviceMarkers] = useState<DeviceMarker[]>([]);
	const lastEventTimeMap = useRef<Map<string, number>>(new Map()).current;

	//Keep the deviceMarkers array up-to-date with the devices parameter
	useEffect(() => {
		setDeviceMarkers((deviceMarkers) => {
			return devices
				.filter((device) => {
					if (siteFloorFilters === undefined) {
						return true;
					}
					return (
						device.site_id === undefined ||
						siteFloorFilters[device.site_id] === undefined ||
						getLocationFloorID(device.location) === undefined ||
						getLocationFloorID(device.location) ===
							siteFloorFilters[device.site_id]
					);
				})
				.map((device): DeviceMarker => {
					//Look up the current device marker
					const oldDeviceMarker = deviceMarkers.find(
						(deviceMarker) => deviceMarker.deviceID === device.id,
					);

					//Look up the site
					const site = sites?.find(
						(site) => site.id === device.site_id,
					);

					return {
						...deviceToDeviceMarker(
							device,
							mapDimensions,
							viewportPadding,
							site,
						),
						// Preserve animation state.
						// Even if the device's location changes, leave the animation in place
						// in case the device location is delayed.
						anim: oldDeviceMarker?.anim,
					};
				});
		});
	}, [devices, setDeviceMarkers, sites, mapDimensions, siteFloorFilters]);

	//Subscribe to movement updates
	useDeviceMovementListener(
		useCallback(
			(event) => {
				setDeviceMarkers((deviceMarkers) => {
					//Find a matching device marker
					const index = deviceMarkers.findIndex(
						(marker) => event.deviceID === marker.deviceID,
					);
					if (index === -1) {
						return deviceMarkers;
					}

					//Get the marker
					const marker = deviceMarkers[index];

					//Make sure the location is newer than the last location
					const eventTimestamp = new Date(event.location.timestamp);
					if (new Date(marker.location.timestamp) >= eventTimestamp) {
						return deviceMarkers;
					}

					//Get the current time
					const timeNow = performance.now();

					//Resolve the animation interval from the last event time
					const lastEventTime = lastEventTimeMap.get(event.deviceID);
					let animInterval: number;
					if (lastEventTime === undefined) {
						animInterval = maxDeviceMovementInterval;
					} else {
						const timeSinceLastEvent = timeNow - lastEventTime;
						animInterval = Math.min(
							Math.max(
								timeSinceLastEvent,
								minDeviceMovementInterval,
							),
							maxDeviceMovementInterval,
						);
					}

					//Record the current time as the last event time
					lastEventTimeMap.set(event.deviceID, timeNow);

					//Start the new segment from the marker's currently rendered
					//position so chained updates don't snap to the previous target
					const startCoords = marker.anim
						? resolveDeviceMarkerCoordinates(marker, timeNow)
						: marker.location.coordinates;

					//Update the marker with the new location
					const updatedDeviceMarkers = [...deviceMarkers];
					updatedDeviceMarkers[index] = {
						...marker,
						location: event.location,
						anim: {
							startCoords: startCoords,
							startDate: timeNow,
							endCoords: event.location.coordinates,
							endDate: timeNow + animInterval,
						},
					};
					return updatedDeviceMarkers;
				});
			},
			[setDeviceMarkers],
		),
	);

	return deviceMarkers;
}
